<!--
  Os preços de combo de UMA unidade (NS 2026-0090). Sem linha ativa a unidade não
  vende o combo; não há preço global de reserva. Moeda: a local da franquia.
-->
<template>
  <data-table
    test-id-prefix="so-combo-prices"
    :config="datatableConfig"
    :data="linhas"
    :endpoint="null"
    @button-click="handleButtonClick"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { error as logError } from '../../utils/DebugLogger';
import { useSOComboPrices } from '../../composables/useSOComboPrices';
import { useMoedaFranquia } from '../../composables/useMoedaFranquia';
import { comboExplanations } from '../so-combos/explanations';
import type { ComboPrices_PriceRow } from '../../contracts/ComboPrices_PriceRow';

const props = defineProps<{
  franchiseId: number | string | null;
  podeEscrever: boolean;
}>();

const emit = defineEmits<{
  edit: [entry: ComboPrices_PriceRow];
  delete: [entry: ComboPrices_PriceRow];
}>();

const { t } = useI18n();
const { fetchByFranchise, save } = useSOComboPrices();
const { formatarMoeda } = useMoedaFranquia();

const EX = computed(() => comboExplanations(t));
const linhas = ref<ComboPrices_PriceRow[]>([]);

const comoLinha = (row: Record<string, unknown>): ComboPrices_PriceRow => row as unknown as ComboPrices_PriceRow;

const colunasDeAcao = computed(() => !props.podeEscrever ? [] : [
  {
    data: null,
    title: t('Editar'),
    width: 110,
    sortable: false,
    renderType: 'clickable_button',
    buttonText: t('Editar'),
    buttonIcon: 'pi pi-pencil',
    buttonAction: 'editar',
    buttonTestId: (row: Record<string, unknown>) => `so-combo-prices-edit-${comoLinha(row).so_combo_price_id}`,
  },
  {
    data: null,
    title: t('Ação'),
    width: 130,
    sortable: false,
    renderType: 'clickable_button',
    buttonText: t('Ligar/Desligar'),
    buttonIcon: 'pi pi-power-off',
    buttonAction: 'alternar',
    buttonTooltip: (row: Record<string, unknown>) =>
      Number(comoLinha(row).so_combo_price_active) === 1
        ? t('Desligar este combo nesta unidade')
        : t('Religar este combo nesta unidade'),
    buttonTestId: (row: Record<string, unknown>) => `so-combo-prices-toggle-${comoLinha(row).so_combo_price_id}`,
  },
]);

const datatableConfig = computed(() => ({
  tableId: 'so-combo-prices',
  rowTestId: (data: Record<string, unknown>) => `so-combo-prices-row-${comoLinha(data).so_combo_price_id}`,
  filename: `so_combo_prices_${new Date().toISOString().split('T')[0]}`,
  columns: [
    { data: 'so_combo_name', title: t('Combo'), sortable: true, filterType: 'text' },
    { data: 'so_service_name', title: t('Serviço'), sortable: true, filterType: 'text' },
    { data: 'so_priority_name', title: t('Prioridade'), width: 150, sortable: true, filterType: 'text' },
    {
      data: 'so_combo_quantity',
      title: t('Pares'),
      width: 90,
      sortable: true,
      renderType: 'integer',
      explanation: EX.value.pares,
    },
    {
      data: 'so_combo_price_price',
      title: t('Preço na unidade'),
      width: 150,
      sortable: true,
      calculated: true,
      explanation: EX.value.precoUnidade,
      cellRenderer: ({ data: row }: { data: Record<string, unknown> }): string =>
        formatarMoeda(comoLinha(row).so_combo_price_price ?? 0, props.franchiseId),
    },
    {
      data: 'so_combo_price_active',
      title: t('Situação'),
      width: 120,
      sortable: true,
      renderType: 'badge',
      explanation: EX.value.precoSituacao,
      badgeConfig: (row: Record<string, unknown>) => {
        const ativo = Number(comoLinha(row).so_combo_price_active) === 1;
        return { label: ativo ? t('Ativo') : t('Desligado'), severity: ativo ? 'success' : 'secondary' };
      },
    },
    ...colunasDeAcao.value,
  ],
  pageLength: 25,
  pagination: true,
  sortable: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const loadData = async (): Promise<void> => {
  if (!props.franchiseId) {
    linhas.value = [];
    return;
  }
  try {
    linhas.value = await fetchByFranchise(Number(props.franchiseId));
  } catch (error: unknown) {
    linhas.value = [];
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar os preços dos combos') + ': ' + message);
  }
};

/**
 * Religar é `salvar` com active=1 sobre a linha existente; desligar abre a
 * confirmação no modal, porque tira o combo da venda da unidade na hora.
 */
const handleButtonClick = async (evento: { data: Record<string, unknown> | null; action?: unknown }): Promise<void> => {
  if (!evento.data) return;
  const linha = comoLinha(evento.data);

  if (evento.action === 'editar') {
    emit('edit', linha);
    return;
  }
  if (evento.action !== 'alternar') return;

  if (Number(linha.so_combo_price_active) === 1) {
    emit('delete', linha);
    return;
  }

  try {
    await save({
      so_combo_price_id: linha.so_combo_price_id,
      so_combo_price_combo_id: Number(linha.so_combo_price_combo_id),
      // A listagem fala UNIDADES; salvar fala CENTAVOS.
      so_combo_price_price: Math.round((linha.so_combo_price_price ?? 0) * 100),
      so_combo_price_active: 1,
    });
    eventBus.emit('globalMessage.success', t('Combo religado nesta unidade'));
    await loadData();
  } catch (error: unknown) {
    logError('SOComboPricesTable', 'handleButtonClick', 'Erro ao religar', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao religar o combo') + ': ' + message);
  }
};

watch(() => props.franchiseId, () => void loadData(), { immediate: true });

defineExpose({ loadData });
</script>
